import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiTrash2 } from "react-icons/fi";
import { getStockLists, createStockList, deleteStockList, uploadFile, createProduct, getCategories } from '../api';
import { normalizePaginatedResponse } from '../utils/pagination';
import useCurrentUser from '../hooks/useCurrentUser';
import { hasPermission } from '../utils/permissions';
import InlineLoading from '../components/InlineLoading';
import { notify } from '../utils/alertBus';
import useConfirmDialog from '../hooks/useConfirmDialog';

const STATIC_CATEGORIES = [
  { id: "chama-fruit", label: "Frutas y Verduras" },
  { id: "chama-max", label: "Construcción" },
  { id: "chama-todo", label: "Otros artículos" },
];

const StockListsView = () => {
  const [loading, setLoading] = useState(true);
  const [stockLists, setStockLists] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [newStockListName, setNewStockListName] = useState('');
  const [formData, setFormData] = useState({
    productName: "",
    productImage: "",
    categoryId: "",
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categoriesError, setCategoriesError] = useState("");
  const { confirm } = useConfirmDialog();
  const { user } = useCurrentUser();
  const navigate = useNavigate();
  const branchId = useParams().branchId || localStorage.getItem('selectedBranchId');
  const categoryOptions = categories.length ? categories : STATIC_CATEGORIES;

  const canCreateStock = hasPermission(user, 'stock.create');
  const canDeleteStock = hasPermission(user, 'stock.delete');
  const canCreateProduct = hasPermission(user, 'product.create');

  useEffect(() => {
    const fetchStockLists = async () => {
      const data = await getStockLists(branchId, undefined, undefined, { pageSize: 0 });
      const normalized = normalizePaginatedResponse(data);
      setStockLists(normalized.data || []);
      setLoading(false);
    };
    fetchStockLists();
  }, []);

  useEffect(() => {
    const fetchCategories = async () => {
      setCategoriesLoading(true);
      setCategoriesError("");
      try {
        const data = await getCategories({ branchId });
        setCategories(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error fetching categories:", error);
        setCategories([]);
        setCategoriesError("No se pudieron cargar las categorías.");
      } finally {
        setCategoriesLoading(false);
      }
    };
    if (branchId) {
      fetchCategories();
    }
  }, [branchId]);

  const handleInputChange = async (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  }

  const handleSave = async (e) => {
    e.preventDefault();
    if (!canCreateProduct) return;
    let response;
    try {
      const resolvedCategory = categoryOptions.find(
        (category) => String(category.id) === String(formData.categoryId)
      );
      const categoryPayload = {
        categoryId: resolvedCategory?.id ?? (formData.categoryId || null),
        category: resolvedCategory?.name || resolvedCategory?.label || "",
      };
      if (formData.productImage.name) {
        const imageUrl = await uploadFile(formData.productImage);
        if (imageUrl.url) {
          setFormData({
            ...formData,
            productImage: imageUrl.url,
          });
          response = await createProduct(branchId, {
            ...formData,
            ...categoryPayload,
            productImage: imageUrl.url,
          });
        }
      } else {
        response = await createProduct(branchId, {
          ...formData,
          ...categoryPayload,
        });
      }
      setMessage(response.message);
      setFormData({ productName: "", productImage: "", categoryId: "" });
      setIsModalOpen(false);
    } catch (error) {
      setMessage(error.message);
    }
  };

  const handleCreateStockList = async () => {
    if (!canCreateStock) return;
    if (!newStockListName.trim()) {
      notify('Por favor ingresa un nombre válido.');
      return;
    }
    await createStockList(branchId, { name: newStockListName });
    setNewStockListName('');
    setModalOpen(false);
    const data = await getStockLists(branchId, undefined, undefined, { pageSize: 0 });
    const normalized = normalizePaginatedResponse(data);
    setStockLists(normalized.data || []);
  };

  const handleDeleteStockList = async (id) => {
    if (!canDeleteStock) return;
    const approved = await confirm({
      title: 'Eliminar lista',
      message: '¿Estás seguro de que deseas eliminar esta lista de compras?',
      confirmLabel: 'Eliminar',
      cancelLabel: 'Cancelar',
      tone: 'danger',
    });
    if (!approved) return;
    await deleteStockList(branchId, id);
    setStockLists((prev) => prev.filter((list) => list.id !== id));
  };

  const getListPaymentSummary = (stockList) => {
    const stocks = stockList?.Stocks || [];
    return stocks.reduce(
      (acc, stock) => {
        const total = Number(stock.price || 0) * Number(stock.quantity || 0);
        const paid = stock.paidAmount !== undefined
          ? Number(stock.paidAmount || 0)
          : (stock.StockPayments || []).reduce((sum, payment) => sum + Number(payment.amount || 0), 0);
        acc.total += total;
        acc.paid += paid;
        acc.balance += Math.max(total - paid, 0);
        return acc;
      },
      { total: 0, paid: 0, balance: 0 }
    );
  };

  return (
    <>
      {isModalOpen && canCreateProduct && (
        <div
          className="fixed inset-0 bg-slate-900/60 flex justify-center items-start z-50 px-4 py-6 overflow-y-auto"
          onClick={() => setIsModalOpen(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl border border-slate-200 overflow-hidden max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 bg-slate-50 border-b border-slate-200 px-6 py-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Nuevo producto
                </p>
                <h2 className="text-xl font-semibold text-slate-900">
                  Agregar producto
                </h2>
              </div>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-slate-500 hover:text-slate-900"
                aria-label="Cerrar"
                type="button"
              >
                ✕
              </button>
            </div>

            <div className="px-6 py-5 space-y-4">
              <form
                className="grid grid-cols-1 md:grid-cols-2 gap-4"
                onSubmit={handleSave}
              >
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Nombre del Producto
                  </label>
                  <input
                    type="text"
                    id="productName"
                    name="productName"
                    value={formData.productName}
                    onChange={handleInputChange}
                    list="product-suggestions"
                    required
                    autoComplete="off"
                    className="border border-slate-300 rounded-lg p-2 w-full focus:ring-2 focus:ring-slate-200"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Categoría
                  </label>
                  <select
                    id="category"
                    name="categoryId"
                    required
                    value={formData.categoryId}
                    onChange={handleInputChange}
                    className="border border-slate-300 rounded-lg p-2 w-full focus:ring-2 focus:ring-slate-200"
                  >
                    <option value="" disabled>Selecciona una categoría</option>
                    {categoryOptions.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name || category.label || `Categoría ${category.id}`}
                      </option>
                    ))}
                  </select>
                  {categoriesLoading && (
                    <p className="text-xs text-gray-500 mt-1">Cargando categorías...</p>
                  )}
                  {categoriesError && !categoriesLoading && (
                    <p className="text-xs text-red-600 mt-1">{categoriesError}</p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Imagen del Producto
                  </label>
                  <input
                    type="file"
                    id="productImage"
                    name="productImage"
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        productImage: e.target.files[0],
                      }))
                    }
                    accept="image/*"
                    className="border border-slate-300 rounded-lg p-2 w-full focus:ring-2 focus:ring-slate-200"
                  />
                </div>

                <div className="md:col-span-2 flex justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="px-4 py-2 rounded-lg bg-white text-slate-700 border border-slate-200 hover:bg-slate-100"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 rounded-lg bg-slate-900 text-white hover:bg-slate-800"
                  >
                    Guardar
                  </button>
                </div>
              </form>

              {message && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-700">
                  {message}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <div className="p-6 max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-semibold">Listas de Compra</h1>
          {canCreateStock && (
            <button
              onClick={() => setModalOpen(true)}
              className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
            >
              Nueva Lista
            </button>
          )}
        </div>

        {canCreateProduct && (
          <button
            onClick={() => setIsModalOpen(true)}
            className="bg-blue-500 text-white px-4 py-2 rounded shadow hover:bg-blue-600 mb-4"
          >
            + Nuevo Producto
          </button>
        )}

        {/* Stock Lists */}
        {loading ? (
          <p>
            <InlineLoading />
          </p>
        ) : (
          <ul className="space-y-4">
            {stockLists.map((stockList) => {
              const summary = getListPaymentSummary(stockList);
              return (
                <li
                  key={stockList.id}
                  className="bg-white shadow rounded p-4 cursor-pointer hover:bg-gray-100 transition"
                  onClick={() => navigate(`/branches/${branchId}/stock/${stockList.id}`)} // Navigate to stock list detail
                >
                  <p className="text-lg font-semibold my-2">{stockList.name}</p>
                  <p className='text-sm text-gray-500 my-2'>{new Date(stockList.createdAt).toLocaleDateString()}</p>
                  <div className="flex flex-wrap gap-2 text-xs text-gray-700 mb-3">
                    <span className="bg-gray-100 px-2 py-1 rounded">
                      Total: ${summary.total.toLocaleString("de-DE")}
                    </span>
                    <span className="bg-green-100 text-green-700 px-2 py-1 rounded">
                      Pagado: ${summary.paid.toLocaleString("de-DE")}
                    </span>
                    {summary.balance > 0 && (
                      <span className="bg-red-100 text-red-600 px-2 py-1 rounded">
                        Pendiente: ${summary.balance.toLocaleString("de-DE")}
                      </span>
                    )}
                  </div>
                  <div className="flex justify-between items-center w-full">
                    {canCreateStock && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/branches/${branchId}/stock/${stockList.id}/create`);
                        }}
                        className="bg-blue-500 text-white px-4 py-1 rounded hover:bg-blue-600"
                      >
                        + Compra
                      </button>
                    )}
                    {canCreateStock && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/branches/${branchId}/stock/${stockList.id}/create-expense`);
                        }}
                        className="bg-yellow-500 text-white px-4 py-1 rounded hover:bg-yellow-600"
                      >
                        + Gasto
                      </button>
                    )}
                    {canDeleteStock && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteStockList(stockList.id);
                        }}
                        className="bg-red-500 text-white px-4 py-1 rounded hover:bg-red-600 flex items-center justify-center"
                      >
                        <FiTrash2 size={20} />
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Modal for Creating Stock List */}
      {modalOpen && canCreateStock && (
        <div
          className="fixed inset-0 bg-slate-900/60 flex justify-center items-start z-50 px-4 py-6 overflow-y-auto"
          onClick={() => setModalOpen(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg border border-slate-200 overflow-hidden max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside modal
          >
            <div className="flex items-start justify-between gap-4 bg-slate-50 border-b border-slate-200 px-6 py-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Nueva lista
                </p>
                <h2 className="text-xl font-semibold text-slate-900">
                  Crear lista de compra
                </h2>
              </div>
              <button
                onClick={() => setModalOpen(false)}
                className="text-slate-500 hover:text-slate-900"
                aria-label="Cerrar"
                type="button"
              >
                ✕
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <input
                type="text"
                value={newStockListName}
                onChange={(e) => setNewStockListName(e.target.value)}
                placeholder="Ingresa el nombre de la lista"
                className="border border-slate-300 rounded-lg p-2 w-full"
              />
            </div>
            <div className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-6 py-4">
              <button
                onClick={() => setModalOpen(false)}
                className="px-4 py-2 rounded-lg bg-white text-slate-700 border border-slate-200 hover:bg-slate-100"
                type="button"
              >
                Cancelar
              </button>
              <button
                onClick={handleCreateStockList}
                className="px-4 py-2 rounded-lg bg-slate-900 text-white hover:bg-slate-800"
                type="button"
              >
                Crear
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default StockListsView;
